import { format, isValid, parse, parseISO } from 'date-fns';

import { DatePickerInputFormat, datePickerIsoFormat } from '@components/library/date-picker';

/**
 * Get whether a string is an ISO-like date (such as 2021-06-30 or 2021-06-30T00:00:00Z).
 * NOTE: Doesn't validate that the ISO date is actually a date.
 * @param isoDate string | null | undefined
 * @returns boolean
 */
export const getIsIsoDate = (isoDate?: string | null): boolean => {
  if (!isoDate) return false;
  // ["2021", "06", "30"]
  return isoDate.split('-').length === 3;
};

interface ValidatedDate {
  date: Date;
  dateIso: string;
  dateValue: string;
  valid: boolean;
}

/**
 * Validates a date object or date string. One of either date or dateString is required.
 * If valid, returns valid as true as well as a date object, ISO-like date, and a formatted date value.
 * if invalid, returns valid as false.
 * @param inputFormat DatePickerInputFormat
 * @param date Date | undefined
 * @param dateString string | undefined
 * @returns ValidatedDate
 */
export const validateDate = (inputFormat: DatePickerInputFormat, date?: Date, dateString?: string): ValidatedDate => {
  const response: ValidatedDate = {
    date: new Date(),
    dateIso: '',
    dateValue: '',
    valid: true
  };

  if (date) {
    if (isValid(date)) {
      const dateIso = format(date, datePickerIsoFormat);
      const dateValue = format(date, inputFormat.format);

      return {
        ...response,
        date,
        dateIso,
        dateValue
      };
    }
  }

  if (dateString) {
    const parsedDate = getIsIsoDate(dateString) ? parseISO(dateString) : parse(dateString, inputFormat.parse, new Date());

    if (isValid(parsedDate)) {
      const dateIso = format(parsedDate, datePickerIsoFormat);
      const dateValue = format(parsedDate, inputFormat.format);

      return {
        ...response,
        date: parsedDate,
        dateIso,
        dateValue
      };
    }
  }

  return {
    ...response,
    valid: false
  };
};

/**
 * Get a JS Date object from an ISO string. Returns null if the input isn't a valid date.
 * @param date Date | string | undefined
 * @returns Date | null
 */
export const getDate = (date?: Date | string): Date | null => {
  if (!date) return null;

  if (typeof date === 'string' && getIsIsoDate(date)) {
    const parsedIsoDate = parseISO(date);
    if (isValid(parsedIsoDate)) {
      return parsedIsoDate;
    }
  }
  if (date instanceof Date && isValid(date)) {
    return date;
  }

  return null;
};
